import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { getImageUrl, getImagePosition } from '../utils/bunnyImage';

// --- MINI-COMPONENT: Handles individual card logic ---
function BunnyCard({ bunny, onOpenModal }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
      // 1. Check if this specific bunny has an array of images
      if (bunny?.images && bunny.images.length > 1) {
        const imageList = bunny.images;

        // 2. Preload the NEXT image in this bunny's array
        const nextIndex = (currentImageIndex + 1) % imageList.length;
        const nextImg = new Image();
        nextImg.src = getImageUrl(imageList[nextIndex]);

        // 3. Preload the PREVIOUS image
        const prevIndex = (currentImageIndex - 1 + imageList.length) % imageList.length;
        const prevImg = new Image();
        prevImg.src = getImageUrl(imageList[prevIndex]);
      }
      // This tells React to preload every time the user clicks to a new image
    }, [currentImageIndex, bunny.images]);

  const nextImage = (e) => {
    e.stopPropagation();
    if (bunny.images) {
      setCurrentImageIndex((prev) => (prev + 1) % bunny.images.length);
    }
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (bunny.images) {
      setCurrentImageIndex((prev) => (prev - 1 + bunny.images.length) % bunny.images.length);
    }
  };

  return (
    <div className="bunny-card bunny-profile-card" onClick={() => onOpenModal(bunny)}>
      <div className="card-image-container bunny-profile-image">
        <div className={`status-tag status-${bunny.status}`}>
          {bunny.status?.toUpperCase() || 'UNKNOWN'}
        </div>

        {bunny.images && bunny.images.length > 1 && (
          <button className="carousel-btn left" onClick={prevImage}>&#10094;</button>
        )}

        {bunny.images && bunny.images.length > 0 ? (
          /* REMOVED: blurred-backdrop div */
          <img
            src={getImageUrl(bunny.images[currentImageIndex])}
            alt={bunny.name}
            className="bunny-card-photo" /* UPDATED CLASS */
            loading="lazy"
            style={{ objectPosition: getImagePosition(bunny.images[currentImageIndex]) }}
          />
        ) : (
          <div className="no-image-placeholder">No Image</div>
        )}

        {bunny.images && bunny.images.length > 1 && (
          <button className="carousel-btn right" onClick={nextImage}>&#10095;</button>
        )}

        <div className="bunny-hover-info">
          <h4>{bunny.name}</h4>
        </div>
      </div>

      <h3>{bunny.name}</h3>
      <p className="bunny-card-region">{bunny.region}</p>
      <p className="bunny-card-type">{bunny.type}</p>
      <p className="bunny-card-price"><strong>{bunny.price}</strong></p>
    </div>
  );
}

// --- MAIN PAGE COMPONENT ---
function AvailableBunnies() {
  const [bunnies, setBunnies] = useState([]);
  const [selectedBunny, setSelectedBunny] = useState(null);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const navigate = useNavigate();

useEffect(() => {
    const fetchBunnies = async () => {
      const bunnyCollection = collection(db, "bunnies");
      const bunnySnapshot = await getDocs(bunnyCollection);
      const bunnyList = bunnySnapshot.docs.map(doc => ({
        id: doc.id, ...doc.data()
      }));

      // 1. Filter out sold/breeding bunnies
      const filteredBunnies = bunnyList.filter(b =>
        b.status === "available" || b.status === "reserved" || b.status === "waitlist"
      );

      // 2. Define the custom sorting rank (1 is highest priority)
      const statusRank = {
        "waitlist": 1,
        "available": 2,
        "reserved": 3
      };

      // 3. Sort the array based on the rank
      filteredBunnies.sort((a, b) => {
        // Fallback to 99 if a status is somehow missing
        const rankA = statusRank[a.status] || 99;
        const rankB = statusRank[b.status] || 99;
        return rankA - rankB;
      });

      // 4. Update the state with the newly sorted list
      setBunnies(filteredBunnies);
    };

    fetchBunnies();
  }, []);

  const openModal = (bunny) => {
    setSelectedBunny(bunny);
    setModalImageIndex(0);
  };

  const closeModal = () => setSelectedBunny(null);

  const nextModalImage = (e) => {
    e.stopPropagation();
    setModalImageIndex((prev) => (prev + 1) % selectedBunny.images.length);
  };

  const prevModalImage = (e) => {
    e.stopPropagation();
    setModalImageIndex((prev) => (prev - 1 + selectedBunny.images.length) % selectedBunny.images.length);
  };

  return (
    <div className="container">
      <Helmet>
        <title>Available Bunnies & Waitlist | Bunny Boutique 321</title>
        <meta name="description" content="View our currently available TeddyWidder bunnies in Melbourne, FL. Reserve your new furry friend or join our waitlist for upcoming litters!" />
        <link rel="canonical" href="https://bunnyboutique321.com/available" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Available Bunnies & Waitlist | Bunny Boutique 321" />
        <meta property="og:description" content="View our currently available TeddyWidder bunnies in Melbourne, FL. Reserve your new furry friend or join our waitlist for upcoming litters!" />
        <meta property="og:url" content="https://bunnyboutique321.com/available" />
        <meta property="og:image" content="https://bunnyboutique321.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Available Bunnies & Waitlist | Bunny Boutique 321" />
        <meta name="twitter:description" content="View our currently available TeddyWidder bunnies in Melbourne, FL. Reserve your new furry friend or join our waitlist for upcoming litters!" />
        <meta name="twitter:image" content="https://bunnyboutique321.com/og-image.jpg" />
      </Helmet>

      <span className="eyebrow">Currently Available</span>
      <h1>Available Bunnies</h1>
      <div className="bunny-grid">
        {bunnies.map((bunny) => (
          <BunnyCard key={bunny.id} bunny={bunny} onOpenModal={openModal} />
        ))}
      </div>

      {/* DETAILED MODAL OVERLAY */}
      {selectedBunny && (
        <div className="modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {/* MODAL CAROUSEL */}
            <div className="modal-image-container">
              <button className="modal-close-btn" onClick={closeModal} aria-label="Close">&times;</button>

              {selectedBunny.images && selectedBunny.images.length > 1 && (
                <button className="carousel-btn left" onClick={prevModalImage}>&#10094;</button>
              )}

             {selectedBunny.images && selectedBunny.images.length > 0 ? (
               /* REMOVED: blurred-backdrop div */
               <img
                 src={getImageUrl(selectedBunny.images[modalImageIndex])}
                 alt={selectedBunny.name}
                 className="foreground-image" /* Keep this class as it's styled for the modal */
                 loading="eager"
                 fetchpriority="high"
                 style={{ objectPosition: getImagePosition(selectedBunny.images[modalImageIndex]) }}
               />
             ) : (
                <div className="no-image-placeholder">No Image</div>
              )}

              {selectedBunny.images && selectedBunny.images.length > 1 && (
                <button className="carousel-btn right" onClick={nextModalImage}>&#10095;</button>
              )}

              {/* Modal Indicator Dots (Keep these as they are helpful in the modal) */}
              {selectedBunny.images && selectedBunny.images.length > 1 && (
                <div style={{ textAlign: 'center', position: 'absolute', bottom: '10px', width: '100%', zIndex: 3 }}>
                  {selectedBunny.images.map((_, idx) => (
                    <span key={idx} style={{ display: 'inline-block', width: '8px', height: '8px', margin: '0 4px', backgroundColor: modalImageIndex === idx ? '#ffffff' : 'rgba(255,255,255,0.5)', borderRadius: '50%', boxShadow: '0 1px 3px rgba(0,0,0,0.5)' }}></span>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-body">
              <h3>{selectedBunny.name}</h3>
              <div className="modal-meta-row">
                <span className="modal-tag modal-tag--muted">{selectedBunny.region}</span>
                <span className="modal-tag">{selectedBunny.type}</span>
                <span className="modal-price"><strong>{selectedBunny.price}</strong></span>
              </div>
              <hr />
              <p className="modal-description">{selectedBunny.description}</p>

              <button
                className="reserve-btn"
                disabled={selectedBunny.status === 'reserved'}
                style={{
                  backgroundColor: selectedBunny.status === 'reserved' ? '#ccc' : '',
                  cursor: selectedBunny.status === 'reserved' ? 'not-allowed' : 'pointer'
                }}
                onClick={() => navigate(`/contact?bunny=${encodeURIComponent(selectedBunny.name)}&status=${encodeURIComponent(selectedBunny.status)}`)}
              >
                {selectedBunny.status === 'reserved'
                  ? `Currently Reserved`
                  : selectedBunny.status === 'waitlist'
                    ? `Join Waitlist for ${selectedBunny.name}`
                    : `Reserve ${selectedBunny.name}`
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AvailableBunnies;